import { api } from './api-client';

type CheckoutParams = {
  tier: string;
  eventBucket: number;
  billingInterval: string;
  bundledPackId?: string;
  // Lean onboarding: Stripe returns to Getting Started instead of /payments/success.
  returnTo?: 'getting-started';
};

type ChangePlanParams = {
  tier: string;
  eventBucket: number;
  billingInterval: string;
};

export type BillingInfo = {
  plan: string;
  eventBucket: number | null;
  billingInterval: 'monthly' | 'yearly' | null;
  eventsUsed: number;
  thresholdPercentage: number;
  domainCount: number;
  domainLimit: number | null;
  memberCount: number;
  memberLimit: number | null;
  active: boolean;
  lastPayment: string | null;
  expiresAt: string | null;
  isTrialing: boolean;
  trialDaysRemaining: number;
  effectivePlan: string;
  pendingChange: {
    plan: string;
    eventBucket: number | null;
    billingInterval: string | null;
    effectiveAt: string;
  } | null;
  pendingCancellation: { cancelAt: string } | null;
  // Set while a paid plan is in its Stripe trial (the first month for $1).
  trialEndsAt?: string | null;
  overageEnabled: boolean;
  // Per-event $ rate charged for events past the monthly cap (0 when not applicable).
  overageRate: number;
};

// Per-action-type usage for the current billing period. `reason` is the raw
// ledger reason (e.g. `action.slack`, `enrich.company`); `count` is the number
// of actions debited under it this period.
export type ActionUsageBreakdown = {
  periodStart: string;
  breakdown: { reason: string; count: number }[];
};

export type PricingMatrix = {
  tiers: string[];
  eventBuckets: number[];
  matrix: Record<
    string,
    Record<string, { priceCents: number; overageRate: number }>
  >;
  planLimits: Record<
    string,
    {
      maxDomains: number | null;
      maxUsers: number | null;
      monthlyEvents?: number;
    }
  >;
  features: Record<string, Record<string, boolean>>;
};

export const PaymentService = {
  getCheckoutUrl: async ({
    tier,
    eventBucket,
    billingInterval,
    bundledPackId,
    returnTo,
  }: CheckoutParams) => {
    const { data } = await api.post<{ data: { url: string } }>(
      '/payments/checkout',
      {
        tier,
        eventBucket,
        billingInterval,
        ...(bundledPackId && { bundledPackId }),
        ...(returnTo && { returnTo }),
      },
    );
    return data.url;
  },

  getPortalUrl: async () => {
    const { data } = await api.post<{ data: { url: string } }>(
      '/payments/portal',
    );
    return data.url;
  },

  changePlan: async (params: ChangePlanParams) => {
    const { data } = await api.post<{ data: Record<string, unknown> }>(
      '/billing/change-plan',
      params,
    );
    return data;
  },

  getCurrentBilling: async () => {
    const result = await api.get<{ data: BillingInfo }>('/billing/current');
    return result.data;
  },

  setOverage: async (enabled: boolean) => {
    const { data } = await api.put<{ data: { overageEnabled: boolean } }>(
      '/billing/overage',
      { enabled },
    );
    return data;
  },

  getActionUsage: async () => {
    const result = await api.get<{ data: ActionUsageBreakdown }>(
      '/billing/actions/usage',
    );
    return result.data;
  },

  getPricingMatrix: async () => {
    const result = await api.get<{ data: PricingMatrix }>('/pricing/matrix');
    return result.data;
  },

  cancelSubscription: async () => {
    const { data } = await api.post<{ data: { cancelAt: string } }>(
      '/billing/cancel-subscription',
    );
    return data;
  },

  cancelPendingChange: async () => {
    await api.post('/billing/cancel-pending-change');
  },

  reactivateSubscription: async () => {
    const { data } = await api.post<{ data: { reactivated: boolean } }>(
      '/billing/reactivate-subscription',
    );
    return data;
  },

  syncSubscription: async (organizationId?: string) => {
    const { data } = await api.post<{
      data: { synced: boolean; message?: string };
    }>(
      '/billing/sync-subscription',
      organizationId ? { organizationId } : {},
    );
    return data;
  },

  getAdminSubscriptions: async () => {
    const result = await api.get<{
      data: {
        subscriptions: AdminSubscription[];
      };
    }>('/billing/admin/subscriptions');
    return result.data.subscriptions;
  },

  migratePaidSubscription: async (params: {
    organizationId: string;
    tier: 'FREE' | 'ESSENTIALS' | 'PRO' | 'ENTERPRISE';
    eventBucket: number;
    billingInterval: 'monthly' | 'yearly';
    couponId?: string;
    // When true, follow /change-plan rules: upgrades proration-bill now,
    // downgrades schedule for end of period. When false (default), apply
    // the change immediately with no proration (admin override).
    prorate?: boolean;
  }) => {
    const { data } = await api.post<{
      data: {
        // 'migrated' = brand-new Stripe sub created.
        // 'updated' = existing active sub modified in place.
        // 'scheduled' = downgrade scheduled for end of current period
        //               (only when caller passed prorate=true).
        status: 'migrated' | 'updated' | 'scheduled' | 'already_migrated';
        // Stripe-side subscription status (active | trialing | incomplete | …).
        // `incomplete` means the customer has no payment method yet — admin
        // needs to follow up via Stripe portal.
        stripeStatus?: string;
        customerId?: string;
        customerWasCreated?: boolean;
        subscriptionId?: string;
        tier?: string;
        eventBucket?: number;
        billingInterval?: string;
        couponId?: string | null;
        // ISO timestamp when a scheduled downgrade takes effect (only set
        // when status is 'scheduled').
        effectiveAt?: string;
        message?: string;
      };
    }>('/billing/admin/migrate-paid-subscription', params);
    return data;
  },

  getStripeCoupons: async () => {
    const result = await api.get<{
      data: { coupons: StripeCoupon[] };
    }>('/billing/admin/coupons');
    return result.data.coupons;
  },

  grantDataCredits: async (params: {
    organizationId: string;
    credits: number;
    idempotencyKey: string;
    note?: string;
  }) => {
    const { data } = await api.post<{
      data: {
        granted: boolean;
        credits: number;
        // Headroom left under the per-org rolling cap after this grant.
        remainingInWindow: number;
      };
    }>('/billing/admin/grant-data-credits', params);
    return data;
  },

  // Pin or clear a per-org data-retention window (months). null retentionMonths clears the override.
  updateRetentionOverride: async (params: {
    organizationId: string;
    retentionMonths: number | null;
  }) => {
    const { data } = await api.put<{
      data: { organizationId: string; retentionMonthsOverride: number | null };
    }>('/billing/admin/retention-override', params);
    return data;
  },
};

export type StripeCoupon = {
  id: string;
  name: string;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: 'forever' | 'once' | 'repeating';
};

export type AdminSubscription = {
  organizationId: string;
  organizationName: string;
  slug: string;
  createdAt: string;
  ownerEmail: string | null;
  plan: string;
  eventBucket: number | null;
  active: boolean;
  customerId: string | null;
  subscriptionId: string | null;
  lastPayment: string | null;
  expiresAt: string | null;
  couponId: string | null;
  retentionMonthsOverride: number | null;
};
